import {
  alphabeticalCompare,
  alphabeticalSort,
  CIMSPage,
  EJStatus,
} from 'shared'
import { CIMSColumnsOptions } from '../../../../../../../../../../../components/CIMSTable/CIMSColumn/types'
import { HelpMessage } from '../../../../../../../../../../../utils/constants'
import { CIMSTag } from '../../../../../../../../../../../components/CIMSTag'
import { EngineeringJudgement } from '../../../../EJLogContext/types'
import { useEJLogContext } from '../../../../EJLogContext'
import { getDeepestString } from '../../../../../../../../../../../utils/getReactNodeLength'
import { useReportsColumn } from './hooks/useReportsColumn'
import { useSingleUserColumn } from '../../../../../../../../../../../components/CIMSTable/common/hooks/useSingleUserColumn'
import StatusButton from './components/StatusButton'
import { useExternalLinks } from '../../../../../../../../../../../components/CIMSTable/common/hooks/useExternalLinks'
import { useGlobalState } from '../../../../../../../../../../../context/GlobalStateContext'
import { getReportName } from './utils'
import { dateFormatter } from '../../../../../../../../../../../utils/dateFormatter'

const Help = HelpMessage.EngineeringJudgements.EJLog

export function useColumns(): CIMSColumnsOptions<EngineeringJudgement> {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const { refetch, edit, typeOptions } = useEJLogContext()
  return {
    columns: [
      {
        title: 'Status',
        help: Help.Status,
        render: (ej) => <StatusButton {...ej} />,
        width: 125,
        filter: {
          type: 'default',
          with: 'items',
          items: EJStatus.sort(alphabeticalSort('priority')).map(
            ({ id, name, description }) => ({
              text: name,
              tooltip: description,
              value: id,
            })
          ),
          key: 'statusId',
        },
        sort: {
          with: 'callback',
          default: 'ascend',
          priority: 0,
          onCompare: (a, b) => {
            const A = EJStatus.fromIdOrFail(a.statusId)
            const B = EJStatus.fromIdOrFail(b.statusId)
            return alphabeticalCompare(A, B, 'priority')
          },
        },
      },
      {
        title: 'Number',
        help: Help.Number,
        render: 'formattedNumber',
        align: 'center',
        width: 135,
        sort: {
          with: 'key',
          key: 'formattedNumber',
          default: 'descend',
          priority: 2,
        },
        filter: { type: 'search', with: 'key', key: 'formattedNumber' },
      },
      useExternalLinks({
        type: 'basic',
        dataFn: ({ externalLinks, identifiedFieldIssue }) => {
          const { module, pathname } = CIMSPage.IFITracker
          return {
            externalLinks,
            prominentLinks: identifiedFieldIssue
              ? [
                  {
                    external: false,
                    label: 'Identified Field Issue',
                    tooltip: getReportName(identifiedFieldIssue),
                    to: `/${projectId}/${module.pathname}/${pathname}/${identifiedFieldIssue.reportId}`,
                  },
                ]
              : [],
          }
        },
      }),
      {
        title: 'Type',
        width: 150,
        help: Help.Type,
        render: ({ type }) => (
          <CIMSTag style={{ verticalAlign: 'top' }}>{type.name}</CIMSTag>
        ),
        filter: {
          type: 'default',
          with: 'callback',
          items: typeOptions.map(({ value, label }) => ({
            text: getDeepestString(label),
            value: value ?? '',
          })),
          onFilter: (value, { type }) => type?.id === value,
        },
        sort: {
          with: 'callback',
          onCompare: (a, b) => alphabeticalCompare(a.type, b.type, 'name'),
        },
      },
      {
        title: 'Failure Date',
        help: Help.FailureDate,
        render: ({ failureDate }) => dateFormatter.simple(failureDate),
        width: 170,
        filter: { type: 'date', with: 'key', key: 'failureDate' },
        sort: {
          with: 'key',
          key: 'failureDate',
          default: 'descend',
          priority: 2,
        },
      },
      useReportsColumn(),
      {
        title: 'Failure Description',
        help: Help.FailureDescription,
        render: ({ failureDescription }) => failureDescription,
        width: 400,
        filter: {
          type: 'search',
          with: 'callback',
          onFilter: (value, { failureDescription }) =>
            !!failureDescription
              ?.toLowerCase()
              .includes(value.toString().toLowerCase()),
        },
      },
      {
        title: 'Justification',
        help: Help.Justification,
        render: ({ justification }) => justification,
        width: 400,
        filter: {
          type: 'search',
          with: 'callback',
          onFilter: (value, { justification }) =>
            !!justification
              ?.toLowerCase()
              .includes(value.toString().toLowerCase()),
        },
      },
      useSingleUserColumn({
        title: 'Issued By',
        help: Help.IssuedBy,
        user: ({ issuedBy }) => issuedBy,
      }),
      {
        title: 'Issued Date',
        help: Help.IssuedDate,
        render: ({ issuedDate }) => dateFormatter.simple(issuedDate),
        width: 170,
        filter: { type: 'date', with: 'key', key: 'issuedDate' },
        sort: {
          with: 'key',
          key: 'issuedDate',
          default: 'descend',
          priority: 2,
        },
      },
    ],
    prominentCustomFields: {
      disableEditWhen: ({ statusId, deleted }) => ({
        alreadyDisabled:
          statusId === EJStatus.Closed.id
            ? {
                why: 'invalid status',
                statusName: EJStatus.Closed.name,
              }
            : false,
        recordInactive: deleted,
      }),
      onCheckBoolean: async ({ id }, input) => {
        await edit({
          variables: {
            input: {
              id,
              customFields: { booleanInputs: [input] },
            },
          },
        })
        refetch()
      },
      onCheckBooleans: async ({ id }, input) => {
        await edit({
          variables: {
            input: {
              id,
              customFields: { listInputs: { checkboxes: [input] } },
            },
          },
        })
        refetch()
      },
    },
  }
}
